import { ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import {
  Alert,
  Badge,
  Code,
  Group,
  ScrollArea,
  Skeleton,
  Spoiler,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconBulb,
  IconCircleX,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import { STEP_RULE_SET_QUERY } from "../graphql/queries";
import { StepRuleSetQueryResult, StepRuleSetView } from "../types";
import type {
  ScriptErrorCode,
  ScriptStackFrame,
  ScriptWarningCode,
  SourceSnippetLine,
} from "./ValidationPanel";
import { ScriptErrorPhase } from "./ValidationPanel";

/**
 * Callout shown above each rule editor reminding the user that the editors are
 * read-only and that mutations happen via presets.
 */
export const PresetCallout = () => (
  <Alert color="blue" icon={<IconInfoCircle size={16} />} variant="light">
    <Text size="sm">
      Granular editing is not supported. Apply a preset to change these rules.
    </Text>
  </Alert>
);

interface LuaBlockProps {
  /** Field name shown above the code block. */
  label: string;
  /** Lua source to render. `null` falls back to a placeholder comment. */
  value: string | null;
  /** Maximum height (px) of the scroll viewport. Defaults to 220. */
  maxHeight?: number;
}

/**
 * Read-only Lua snippet display with a labelled header. Used by every editor
 * that needs to show a piece of Lua source (conditions, formulas, scripts).
 *
 * `Code` renders its children as text via React's normal escaping, so untrusted
 * Lua source cannot inject HTML.
 */
export const LuaBlock = ({ label, value, maxHeight = 220 }: LuaBlockProps) => (
  <Stack gap={4}>
    <Group gap="xs">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Badge variant="outline" size="xs" color="indigo">
        Lua
      </Badge>
    </Group>
    <ScrollArea.Autosize mah={maxHeight}>
      <Code block>{value ?? "-- (none)"}</Code>
    </ScrollArea.Autosize>
  </Stack>
);

interface UseStepRuleSetResult {
  /** `null` while loading or when no rule set is configured for the step. */
  ruleSet: StepRuleSetView | null;
  /**
   * A React element to render instead of the editor body when the rule set is
   * unavailable (missing step, loading, network error, or unconfigured).
   * `null` once `ruleSet` can be rendered.
   */
  fallback: ReactNode;
}

interface UseStepRuleSetOptions {
  /** Title to show in the error/empty state placeholders. */
  title: string;
  /** Icon rendered next to the title in the empty state. */
  icon: ReactNode;
  /** Skeleton heights rendered while the first query is in flight. */
  skeletonHeights: readonly number[];
}

/**
 * Shared hook that powers every read-only rule-set editor. It:
 *  - resolves the step id from the route
 *  - runs the `STEP_RULE_SET_QUERY`
 *  - returns a pre-rendered `fallback` for every non-happy-path state
 *
 * Callers only need to render the editor body once `ruleSet` is non-null.
 */
export const useStepRuleSet = ({
  title,
  icon,
  skeletonHeights,
}: UseStepRuleSetOptions): UseStepRuleSetResult => {
  const stepId = useParams().stepId ?? "";

  const { data, loading, error } = useQuery<StepRuleSetQueryResult>(
    STEP_RULE_SET_QUERY,
    {
      variables: { tournamentStepId: stepId },
      skip: !stepId,
    },
  );

  if (!stepId) {
    return {
      ruleSet: null,
      fallback: (
        <Alert
          color="yellow"
          icon={<IconAlertCircle size={16} />}
          variant="light"
        >
          No tournament step selected
        </Alert>
      ),
    };
  }

  if (loading && !data) {
    return {
      ruleSet: null,
      fallback: (
        <Stack>
          {skeletonHeights.map((height, i) => (
            <Skeleton key={`sk-${i}`} height={height} />
          ))}
        </Stack>
      ),
    };
  }

  if (error) {
    return {
      ruleSet: null,
      fallback: (
        <Stack>
          <Title order={2}>{title}</Title>
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Failed to load: {error.message}
          </Alert>
        </Stack>
      ),
    };
  }

  const ruleSet = data?.stepRuleSet ?? null;

  if (!ruleSet) {
    return {
      ruleSet: null,
      fallback: (
        <Stack>
          <Group gap="xs">
            {icon}
            <Title order={2}>{title}</Title>
          </Group>
          <Alert
            color="yellow"
            icon={<IconAlertCircle size={16} />}
            variant="light"
          >
            No rule set exists yet for this step. Apply a preset to configure{" "}
            {title.toLowerCase()}.
          </Alert>
        </Stack>
      ),
    };
  }

  return { ruleSet, fallback: null };
};

/**
 * Shape shared by `ScriptErrorModel` and `ScriptWarningModel` from the GraphQL
 * schema. Both panels (validation + simulation) render the same alert card for
 * each issue, so we use the same primitive.
 *
 * The `code` is a machine-readable enum from the backend. All text is rendered
 * via React text nodes (no `dangerouslySetInnerHTML`), so untrusted messages
 * from the validator cannot inject HTML.
 *
 * `line` is nullable because the simulator cannot always recover a line for
 * errors raised inside the host-injected prelude. The simulator-specific
 * fields (`phase`, `originHelper`, `hint`, `snippet`, `stackFrames`) are
 * optional because validator issues do not populate them.
 */
export interface ScriptIssue {
  line: number | null;
  column: number;
  message: string;
  code: ScriptErrorCode | ScriptWarningCode;
  phase?: ScriptErrorPhase | null;
  originHelper?: string | null;
  hint?: string | null;
  snippet?: ReadonlyArray<SourceSnippetLine> | null;
  stackFrames?: ReadonlyArray<ScriptStackFrame> | null;
}

interface ScriptIssueListProps {
  issues: readonly ScriptIssue[];
  severity: "error" | "warning";
  /** Stable key prefix so two lists rendered in the same parent do not collide. */
  keyPrefix: string;
}

/** Human-readable label for an execution phase. */
const phaseLabel = (phase: ScriptErrorPhase): string => {
  switch (phase) {
    case ScriptErrorPhase.Prelude:
      return "prelude";
    case ScriptErrorPhase.Bootstrap:
      return "bootstrap";
    case ScriptErrorPhase.UserScript:
      return "user script";
    case ScriptErrorPhase.Unknown:
      return "unknown";
  }
};

/** Mantine color for an execution phase badge. */
const phaseColor = (phase: ScriptErrorPhase): string => {
  switch (phase) {
    case ScriptErrorPhase.Prelude:
      return "yellow";
    case ScriptErrorPhase.Bootstrap:
      return "orange";
    case ScriptErrorPhase.UserScript:
      return "gray";
    case ScriptErrorPhase.Unknown:
      return "gray";
  }
};

/** Column width used to right-align line numbers in the source snippet. */
const LINE_NUMBER_WIDTH = 4;

/** Pad a line number to a fixed width so snippets line up vertically. */
const padLineNumber = (n: number): string =>
  String(n).padStart(LINE_NUMBER_WIDTH, " ");

/**
 * Discriminated union returned by `parseErrorAttribution`. When the simulator
 * runs a PRESET-context script, it auto-simulates every inner `add_rule(...)`
 * condition, every `EXECUTE_LUA` action body, and every `add_tiebreaker(...)`
 * formula. Errors raised by those inner bodies are folded into the outer
 * `errors[]` array with their `message` prefixed with attribution describing
 * which rule / action / tiebreaker the error came from. We render that
 * attribution as a Mantine `Badge` so the rest of the message stays clean.
 *
 * The `remainder` field always holds the message stripped of its prefix —
 * even on the `no_attribution` branch — so callers can render it without
 * branching on the kind a second time.
 */
export type ParsedAttribution =
  | {
      readonly kind: "rule_condition";
      readonly ruleName: string;
      readonly remainder: string;
    }
  | {
      readonly kind: "rule_action";
      readonly ruleName: string;
      readonly actionIndex: number;
      readonly remainder: string;
    }
  | {
      readonly kind: "tiebreaker_formula";
      readonly tiebreakerType: string;
      readonly remainder: string;
    }
  | {
      readonly kind: "no_attribution";
      readonly remainder: string;
    };

/**
 * Parse an attribution prefix off the start of an inner-body error message.
 *
 * Recognised prefixes (produced by the backend's recursive PRESET validator):
 *   "In rule '<name>' condition: <inner>"
 *   "In rule '<name>' action #<n>: <inner>"
 *   "In tiebreaker '<type>' formula: <inner>"
 *
 * Returns a discriminated union; the `remainder` field contains the message
 * with the prefix stripped (or the full message when no prefix matched).
 */
export const parseErrorAttribution = (message: string): ParsedAttribution => {
  // Rule condition: "In rule '<name>' condition: <rest>"
  const ruleConditionMatch = message.match(
    /^In rule '([^']*)' condition: ([\s\S]*)$/,
  );
  if (ruleConditionMatch) {
    const [, ruleName, remainder] = ruleConditionMatch;
    if (ruleName !== undefined && remainder !== undefined) {
      return { kind: "rule_condition", ruleName, remainder };
    }
  }

  // Rule action: "In rule '<name>' action #<n>: <rest>"
  const ruleActionMatch = message.match(
    /^In rule '([^']*)' action #(\d+): ([\s\S]*)$/,
  );
  if (ruleActionMatch) {
    const [, ruleName, indexText, remainder] = ruleActionMatch;
    if (
      ruleName !== undefined &&
      indexText !== undefined &&
      remainder !== undefined
    ) {
      const actionIndex = Number.parseInt(indexText, 10);
      if (Number.isInteger(actionIndex)) {
        return { kind: "rule_action", ruleName, actionIndex, remainder };
      }
    }
  }

  // Tiebreaker formula: "In tiebreaker '<type>' formula: <rest>"
  const tiebreakerFormulaMatch = message.match(
    /^In tiebreaker '([^']*)' formula: ([\s\S]*)$/,
  );
  if (tiebreakerFormulaMatch) {
    const [, tiebreakerType, remainder] = tiebreakerFormulaMatch;
    if (tiebreakerType !== undefined && remainder !== undefined) {
      return { kind: "tiebreaker_formula", tiebreakerType, remainder };
    }
  }

  return { kind: "no_attribution", remainder: message };
};

/**
 * Short, human-readable label for an attribution badge, derived from a
 * non-`no_attribution` `ParsedAttribution` value. Examples:
 *   - "Rule: cumulative-rank · condition"
 *   - "Rule: cumulative-rank · action #1"
 *   - "Tiebreaker: head_to_head formula"
 */
const attributionBadgeLabel = (
  attribution: Exclude<ParsedAttribution, { kind: "no_attribution" }>,
): string => {
  switch (attribution.kind) {
    case "rule_condition":
      return `Rule: ${attribution.ruleName} · condition`;
    case "rule_action":
      return `Rule: ${attribution.ruleName} · action #${attribution.actionIndex}`;
    case "tiebreaker_formula":
      return `Tiebreaker: ${attribution.tiebreakerType} formula`;
    default: {
      const _exhaustive: never = attribution;
      throw new Error(`Unhandled attribution kind: ${String(_exhaustive)}`);
    }
  }
};

/** Format a single stack frame as a one-line description. */
const formatStackFrame = (frame: ScriptStackFrame): string => {
  const where = (() => {
    switch (frame.phase) {
      case ScriptErrorPhase.Prelude:
        return frame.helperName !== null && frame.helperName !== undefined
          ? `prelude helper ${frame.helperName}${
              frame.line !== null && frame.line !== undefined
                ? ` (line ${frame.line})`
                : ""
            }`
          : "prelude";
      case ScriptErrorPhase.UserScript:
        return frame.line !== null && frame.line !== undefined
          ? `user line ${frame.line}`
          : "user script";
      case ScriptErrorPhase.Bootstrap:
        return "bootstrap";
      case ScriptErrorPhase.Unknown:
        return "unknown";
    }
  })();
  return `${frame.description} (${where})`;
};

/**
 * Renders a vertical list of validator/simulator issues as Mantine alerts.
 * Extracted so `ValidationPanel` and `SimulationPanel` share the exact same
 * markup and cannot drift in styling.
 */
export const ScriptIssueList = ({
  issues,
  severity,
  keyPrefix,
}: ScriptIssueListProps) => {
  const color = severity === "error" ? "red" : "yellow";
  const Icon = severity === "error" ? IconCircleX : IconAlertTriangle;
  return (
    <>
      {issues.map((issue, i) => {
        const lineDisplay =
          issue.line !== null && issue.line !== undefined
            ? `${issue.line}:${issue.column}`
            : `?:${issue.column}`;
        const keyLine = issue.line ?? "null";
        const phase = issue.phase ?? null;
        const snippet = issue.snippet ?? null;
        const stackFrames = issue.stackFrames ?? null;
        const attribution = parseErrorAttribution(issue.message);
        return (
          <Alert
            key={`${keyPrefix}-${keyLine}-${issue.column}-${issue.code}-${i}`}
            icon={<Icon size={16} />}
            color={color}
            variant="light"
          >
            <Stack gap={6}>
              <Group gap="xs" align="center" wrap="wrap">
                <Code>{lineDisplay}</Code>
                <Badge color={color} size="xs" variant="filled">
                  {issue.code}
                </Badge>
                {phase && (
                  <Badge variant="light" size="xs" color={phaseColor(phase)}>
                    {phaseLabel(phase)}
                  </Badge>
                )}
                {phase === ScriptErrorPhase.Prelude && issue.originHelper && (
                  <Text size="xs" c="dimmed">
                    in helper {issue.originHelper}()
                  </Text>
                )}
                {attribution.kind !== "no_attribution" && (
                  <Badge variant="outline" size="xs" color="violet">
                    {attributionBadgeLabel(attribution)}
                  </Badge>
                )}
              </Group>
              <Text size="sm">{attribution.remainder}</Text>

              {issue.hint && (
                <Group gap={6} mt={4} wrap="nowrap" align="flex-start">
                  <IconBulb
                    size={14}
                    color="var(--mantine-color-blue-6)"
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <Text size="xs" c="blue.7">
                    {issue.hint}
                  </Text>
                </Group>
              )}

              {snippet && snippet.length > 0 && (
                <ScrollArea.Autosize mah={180}>
                  <Code block>
                    {snippet
                      .map((line) => {
                        const prefix = line.isErrorLine ? ">" : " ";
                        return `${prefix} ${padLineNumber(line.lineNumber)}  ${line.source}`;
                      })
                      .join("\n")}
                  </Code>
                </ScrollArea.Autosize>
              )}

              {stackFrames && stackFrames.length > 0 && (
                <Spoiler
                  maxHeight={0}
                  showLabel="Show stack"
                  hideLabel="Hide stack"
                >
                  <Stack gap={2} mt={4}>
                    {stackFrames.map((frame, j) => (
                      <Text
                        key={`${keyPrefix}-${keyLine}-${i}-frame-${j}`}
                        size="xs"
                        c="dimmed"
                        ff="monospace"
                      >
                        {formatStackFrame(frame)}
                      </Text>
                    ))}
                  </Stack>
                </Spoiler>
              )}
            </Stack>
          </Alert>
        );
      })}
    </>
  );
};
